import { XMarkIcon } from "@heroicons/react/24/outline";
import { useEffect, useState, useRef } from "react";
import { createPortal } from "react-dom";
import { classNames } from "src/helpers/classNames";
import { motion, AnimatePresence } from "framer-motion";
import { acquireModalLayer, releaseModalLayer, getCurrentMaxLayer } from "src/helpers/modalLayerManager";
import useOnClickOutside from "src/hooks/useOnClickOutside";

const SIZE_OPTIONS = {
  xs: "max-w-xs",
  sm: "max-w-sm",
  md: "max-w-md",
  lg: "max-w-lg",
  xl: "max-w-xl",
  "2xl": "max-w-2xl",
  "3xl": "max-w-3xl",
  "4xl": "max-w-4xl",
  "5xl": "max-w-5xl",
  "6xl": "max-w-6xl",
  "7xl": "max-w-7xl",
  "8xl": "max-w-8xl",
  full: "max-w-full",
};

export default function SlideOver({
  zIndex = null,
  isOpen = false,
  handleClose = () => {},
  title = "",
  description = "",
  size = "2xl",
  maxWidth,
  position = "right",
  overlay = true,
  showCloseIcon = true,
  closable = true,
  hideHeader = false,
  onOpenChange,
  className = "",
  headerClassName = "",
  bodyClassName = "",
  footerClassName = "",
  headerComponent,
  footerComponent,
  topActionButton = null,
  children,
}) {
  const [open, setOpen] = useState(isOpen);
  const [isGiantScreen, setIsGiantScreen] = useState(false);
  const [modalLayer, setModalLayer] = useState(0);

  const panelRef = useRef(null);

  const sizeClass = (() => {
    if (maxWidth) {
      if (typeof maxWidth === "string") return maxWidth;
      if (typeof maxWidth === "number") return `max-w-[${maxWidth}px]`;
    }
    return SIZE_OPTIONS[size] || SIZE_OPTIONS["2xl"];
  })();

  const positionOptions = {
    left: {
      containerClass: "left-0 flex max-w-full sm:pr-16",
      enterFrom: "-translate-x-full",
      enterTo: "translate-x-0",
      leaveFrom: "translate-x-0",
      leaveTo: "-translate-x-full",
    },
    right: {
      containerClass: "right-0 flex max-w-full sm:pl-16",
      enterFrom: "translate-x-full",
      enterTo: "translate-x-0",
      leaveFrom: "translate-x-0",
      leaveTo: "translate-x-full",
    },
    top: {
      containerClass: "top-0 flex max-h-full pb-10 sm:pb-16",
      enterFrom: "-translate-y-full opacity-0",
      enterTo: "translate-y-0 opacity-100",
      leaveFrom: "translate-y-0 opacity-100",
      leaveTo: "-translate-y-full opacity-0",
    },
    bottom: {
      containerClass: "bottom-0 flex max-h-full pt-10 sm:pt-16",
      enterFrom: "translate-y-full opacity-0",
      enterTo: "translate-y-0 opacity-100",
      leaveFrom: "translate-y-0 opacity-100",
      leaveTo: "translate-y-full opacity-0",
    },
  };

  const positionConfig = positionOptions[position] || positionOptions.right;

  const overlayVariants = {
    hidden: { opacity: 0 },
    visible: { opacity: 1, transition: { duration: 0.2 } },
    exit: { opacity: 0, transition: { duration: 0.2 } },
  };

  useEffect(() => {
    setOpen(isOpen);
    onOpenChange?.(isOpen);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  useEffect(() => {
    const handleResize = () => {
      setIsGiantScreen(window.innerWidth > 1920);
    };

    handleResize();

    window.addEventListener("resize", handleResize);
    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  useEffect(() => {
    const handleEscape = (event) => {
      if (event.key === "Escape" && open && closable) {
        const currentMaxLayer = parseInt(document.body.dataset.maxModalLayer || "0", 10);
        if (modalLayer === currentMaxLayer) {
          handleOnClose();
        }
      }
    };

    if (open) {
      document.addEventListener("keydown", handleEscape);
    }

    return () => {
      document.removeEventListener("keydown", handleEscape);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, modalLayer, closable]);

  useEffect(() => {
    if (open) {
      const newLayer = acquireModalLayer();
      setModalLayer(newLayer);
    }
    return () => {
      if (open) releaseModalLayer();
    };
  }, [open]);

  const handleOnClose = () => {
    if (modalLayer !== getCurrentMaxLayer()) return;
    setOpen(false);
    handleClose();
  };

  useOnClickOutside(
    panelRef,
    (event) => {
      if (event.target.closest(".Toastify")) return;
      if (event.target.closest(".flatpickr-calendar")) return;
      if (event.target.closest(".storage-upload-popup")) return;

      if (overlay && open && closable) {
        handleOnClose();
      }
    },
    modalLayer,
  );

  const slideOverContent = (
    <AnimatePresence>
      {open && (
        <motion.div
          className="relative"
          style={{ zIndex: zIndex ? zIndex : 60 + modalLayer }}
          initial="hidden"
          animate="visible"
          exit="exit"
          role="dialog"
          aria-modal="true"
          aria-labelledby={title ? "slideover-title" : undefined}
          aria-describedby={description ? "slideover-description" : undefined}>
          {overlay && (
            <motion.div
              className="fixed inset-0 bg-gray-400 bg-opacity-40"
              initial="hidden"
              animate="visible"
              exit="exit"
              variants={overlayVariants}
            />
          )}

          <div className="fixed inset-0 overflow-hidden">
            <div className="absolute inset-0 overflow-hidden">
              <div className={classNames("pointer-events-none fixed inset-y-0", positionConfig.containerClass)}>
                <motion.div
                  ref={panelRef}
                  className={classNames("pointer-events-auto flex w-screen flex-col", position === "top" || position === "bottom" ? "h-auto" : `${sizeClass}`, className)}
                  variants={positionConfig.panelVariants}>
                  <div className={classNames("flex flex-col overflow-auto bg-white shadow-xl", isGiantScreen ? "rounded-tl-xl" : "md:rounded-tl-xl", position === "top" || position === "bottom" ? "mt-auto max-h-screen rounded-t-2xl" : "h-full")}>
                    {!hideHeader && (
                      <div className={classNames("border-highlightColor/40 sticky top-0 z-50 flex-shrink-0 rounded-tl-xl border-b bg-gray-50 px-4 sm:px-6 sm:pr-3", topActionButton ? "py-2.5" : "py-3", headerClassName)}>
                        <div className="relative flex items-end justify-between space-x-3">
                          {headerComponent || (
                            <>
                              <div className="animate-fade-in">
                                <div>
                                  <h2
                                    id="slideover-title"
                                    className="text-sm font-semibold leading-6 text-highlightColor">
                                    {title}
                                  </h2>
                                </div>
                                {description && (
                                  <p
                                    id="slideover-description"
                                    className="text-xs text-gray-500">
                                    {description}
                                  </p>
                                )}
                              </div>
                              {showCloseIcon && (
                                <div className="flex h-7 items-center">
                                  <button
                                    type="button"
                                    version="default"
                                    className="text-gray-400 hover:text-gray-500"
                                    onClick={handleOnClose}>
                                    <span className="sr-only">Close panel</span>
                                    <XMarkIcon
                                      className="h-6 w-6"
                                      aria-hidden="true"
                                    />
                                  </button>
                                </div>
                              )}
                              {topActionButton && topActionButton}
                            </>
                          )}
                        </div>
                      </div>
                    )}
                    <div className={classNames("scrollbar flex max-h-full flex-1 flex-col overflow-y-auto p-4 sm:p-7", bodyClassName)}>{children}</div>
                    {footerComponent && <div className={classNames("border-highlightColor/40 sticky bottom-0 z-50 border-t bg-gray-50 px-4 py-4 sm:px-6", footerClassName)}>{footerComponent}</div>}
                  </div>
                </motion.div>
              </div>
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );

  if (typeof document === "undefined") {
    return null;
  }

  return createPortal(slideOverContent, document.body);
}
